import { RootState } from '../../../../store/store';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const API_HOST = process.env.REACT_APP_API_HOST;

export interface Order {
  _id?: string;
  [key: string]: any;
}

export interface OrderCreateRequest {
  [key: string]: any;
}

export interface OrderUpdateRequest {
  [key: string]: any;
}

/**
 * Order API (RTK Query)
 * Infrastructure layer - handles HTTP communication
 */
export const orderApi = createApi({
  reducerPath: 'orderApi',
  baseQuery: fetchBaseQuery({
    baseUrl: API_HOST,
    credentials: 'include',
    prepareHeaders: async (headers, { getState }) => {
      const token = (getState() as RootState).user.info?.api_token;
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Order'],
  endpoints: (build) => ({
    getOrders: build.query<{ data: Order[]; total: number }, { page?: number; limit?: number; status?: string; sortBy?: string; sortOrder?: string }>({
      query: (params) => {
        const query = new URLSearchParams();
        if (params.page) query.append('page', params.page.toString());
        if (params.limit) query.append('limit', params.limit.toString());
        if (params.status) query.append('status', params.status);
        if (params.sortBy) query.append('sortBy', params.sortBy);
        if (params.sortOrder) query.append('sortOrder', params.sortOrder);

        return {
          url: `user-order/list?${query.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['Order'],
    }),

    getOrderById: build.query<Order, string>({
      query: (id) => ({
        url: `order/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: 'Order', id }],
    }),

    createOrder: build.mutation<Order, OrderCreateRequest>({
      query: (data) => ({
        url: 'order/create',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Order'],
    }),

    updateOrder: build.mutation<Order, { id: string; data: OrderUpdateRequest }>({
      query: ({ id, data }) => ({
        url: `order/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Order', id }, 'Order'],
    }),

    deleteOrder: build.mutation<void, string>({
      query: (id) => ({
        url: `order/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Order'],
    }),

    getOrderByCode: build.query<Order, { code: string; params?: any }>({
      query: ({ code, params }) => {
        const query = new URLSearchParams();
        if (params) {
          Object.entries(params).forEach(([key, value]) => {
            query.append(key, value.toString());
          });
        }

        return {
          url: `user-order/code/${code}?${query.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['Order'],
    }),

    useOrder: build.mutation<Order, string>({
      query: (id) => ({
        url: `user-order/use/${id}`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: ['Order'],
    }),

    markOrderAsPaid: build.mutation<Order, string>({
      query: (id) => ({
        url: `user-order/paid/${id}`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: ['Order'],
    }),
  }),
});

export const {
  useGetOrdersQuery,
  useGetOrderByIdQuery,
  useCreateOrderMutation,
  useUpdateOrderMutation,
  useDeleteOrderMutation,
  useGetOrderByCodeQuery,
  useUseOrderMutation,
  useMarkOrderAsPaidMutation,
} = orderApi;

